import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CloseIcon } from '../../../assets/images/CrossIcon.svg';
import { ReactComponent as EmailIcon } from '../../../assets/images/EmailFormIcon.svg';
import { ReactComponent as PersonIcon } from '../../../assets/images/PersonIcon.svg';
import ExternalLinkIcon from '../../../assets/svgComponents/ExternalLinkIcon.jsx';
import CustomPhoneInput from '../../../components/shared/CustomPhoneInput/CustomPhoneInput';
import CustomTextField from '../../../components/shared/CustomTextField/CustomTextField';
import Modal from '../../../components/shared/Modal/Modal';
import { normalizeURL, validateEmail } from '../../../helpers/commonHelper';
import useReferralProviderData from '../../../hooks/useReferralProviderData';
import { saveCandidateInfo } from '../../../redux/actions/referRequestActions';

import styles from './CandidateInfoModal.module.scss';

const CandidateInfoModal = ({ showModal, setShowModal, organization, jobTitle, job, referralProvider }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.authReducer.currentUser);
  const { referralProviderData } = useReferralProviderData();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone_number: '',
  });
  const [emailError, setEmailError] = useState('');

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (key === 'email') {
      setEmailError(validateEmail(value) ? '' : t('validation:email.invalid'));
    }
  };

  const isFormValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.phone_number.trim() !== '' &&
    validateEmail(formData.email);

  const handleContinue = () => {
    if (isFormValid) {
      const formValues = {
        ...formData,
        from_id: referralProviderData?.user?.id ? referralProviderData?.user?.id : currentUser?.id,
        job_id: job?.id,
        full_name: `${formData?.firstName} ${formData?.lastName}`,
        phone_number: formData?.phone_number,
        referrer_id: referralProvider?.id,
      };

      dispatch(
        saveCandidateInfo(formValues, () => {
          setShowModal(false);
          if (job?.external_job_link) {
            // Use setTimeout to avoid iOS Safari popup blocking
            setTimeout(() => {
              const link = job.external_job_link;
              const url = normalizeURL(link);
              window.open(url, '_blank');
            }, 100);
          }
        })
      );
    }
  };

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} centered className={styles.candidateInfoModal}>
      <Modal.CustomHeader>
        <Modal.Title>{t('modals.letsGetStarted')}</Modal.Title>
        <button type="button" onClick={() => setShowModal(false)} className={styles.closeBtn}>
          <CloseIcon />
        </button>
      </Modal.CustomHeader>

      <Modal.Body className={styles.modalContent}>
        <h4 className={styles.thankYouMessage}>{t('browse.thanksForInterest')}</h4>
        <p className={styles.applicationInfo}>{t('browse.excitedAboutInterest', { jobTitle, organization })}</p>

        <div className={styles.formFields}>
          <CustomTextField
            label={t('labels.firstNameRequired')}
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            CustomIcon={PersonIcon}
          />

          <CustomTextField
            label={t('labels.lastNameRequired')}
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            CustomIcon={PersonIcon}
          />

          <div className={styles.inputWrapper}>
            <CustomTextField
              label={t('labels.emailAddressRequired')}
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              CustomIcon={EmailIcon}
              error={!!emailError}
            />
            {emailError && <span className={styles.errorText}>{emailError}</span>}
          </div>

          <CustomPhoneInput
            value={formData.phone_number}
            handleChange={(phone) => handleChange('phone_number', phone)}
            placeholder={t('placeholders.phoneNumber')}
          />

          <button
            type="submit"
            onClick={handleContinue}
            className={`${styles.continueBtn} ${!isFormValid ? styles.disabled : ''}`}
            disabled={!isFormValid}
          >
            <span className={styles.continueText}>{t('browse.continueApplication')}</span>
            <ExternalLinkIcon />
          </button>

          <p className={styles.footerText}>{t('browse.redirectNotice', { organization })}</p>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CandidateInfoModal;
